import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ReactNode } from "react";
import {
	ChevronDownIcon,
	LayoutGridIcon,
	MessageSquareIcon,
	MessageSquarePlusIcon,
	PanelLeftIcon,
	SearchIcon,
} from "lucide-react";
import { AgentAvatar } from "./AgentAvatar.js";
import { PrimaryNav } from "./PrimaryNav.js";
import { cn } from "./cn.js";
import type { WorkspaceMode } from "./desk-state.js";
import { AGENTS, type AgentId } from "./nav.js";
import type { PrimaryNav as PrimaryNavId } from "./nav.js";

type Props = {
	leftOpen: boolean;
	onToggleLeft: () => void;
	workspace: WorkspaceMode;
	onWorkspaceChange: (mode: WorkspaceMode) => void;
	nav: PrimaryNavId;
	setupDone: boolean;
	onNavChange: (next: PrimaryNavId) => void;
	onOpenSearch: () => void;
	/** Open the master Mairko chat (Chat interface). */
	onOpenChat: () => void;
	/** Start a fresh master chat thread. */
	onNewChat: () => void;
	/** Open a specialist room on the Desk. */
	onAskAgent: (id: AgentId) => void;
	children?: ReactNode;
};

/** Sidebar · Chat/Desk mode · desk tabs · search · Chat / New chat. */
export function ShellToolbar({
	leftOpen,
	onToggleLeft,
	workspace,
	onWorkspaceChange,
	nav,
	setupDone,
	onNavChange,
	onOpenSearch,
	onOpenChat,
	onNewChat,
	onAskAgent,
	children,
}: Props) {
	const askable = AGENTS.filter(
		(a) =>
			a.availability === "live" ||
			(!setupDone && a.id === "onboarding"),
	);

	return (
		<div className="shell-toolbar">
			<div className="shell-toolbar-left">
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							type="button"
							variant="ghost"
							size="icon-sm"
							className="shell-panel-btn"
							aria-pressed={leftOpen}
							aria-label={leftOpen ? "Hide sidebar" : "Show sidebar"}
							onClick={onToggleLeft}
						>
							<PanelLeftIcon className="size-4" />
						</Button>
					</TooltipTrigger>
					<TooltipContent side="bottom">
						{leftOpen ? "Hide sidebar" : "Show sidebar"} · ⌘B
					</TooltipContent>
				</Tooltip>

				<nav className="workspace-modes" aria-label="Interface">
					<button
						type="button"
						className={cn(
							"workspace-mode",
							workspace === "chat" && "workspace-mode--active",
						)}
						aria-current={workspace === "chat" ? "page" : undefined}
						onClick={() => onWorkspaceChange("chat")}
					>
						<MessageSquareIcon className="size-3.5" aria-hidden />
						Chat
					</button>
					<button
						type="button"
						className={cn(
							"workspace-mode",
							workspace === "desk" && "workspace-mode--active",
						)}
						aria-current={workspace === "desk" ? "page" : undefined}
						onClick={() => onWorkspaceChange("desk")}
					>
						<LayoutGridIcon className="size-3.5" aria-hidden />
						Desk
					</button>
				</nav>

				{workspace === "desk" ? (
					<PrimaryNav
						value={nav}
						setupDone={setupDone}
						onChange={onNavChange}
					/>
				) : null}
				{children}
			</div>

			<button
				type="button"
				className="shell-search"
				onClick={onOpenSearch}
				aria-label="Search"
			>
				<SearchIcon className="size-3.5 shrink-0 opacity-60" />
				<span className="shell-search-placeholder">Search</span>
				<kbd className="shell-search-kbd">⌘K</kbd>
			</button>

			<div className="shell-toolbar-right">
				<div className="shell-ask-group">
					<Button
						type="button"
						size="sm"
						className="shell-ask-btn h-8 gap-1.5 rounded-r-none"
						onClick={() => {
							if (workspace === "chat") onNewChat();
							else onOpenChat();
						}}
					>
						<MessageSquarePlusIcon className="size-3.5" />
						{workspace === "chat" ? "New chat" : "Chat"}
					</Button>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								type="button"
								size="sm"
								className="shell-ask-btn h-8 rounded-l-none border-l border-black/10 px-1.5"
								aria-label="More chat options"
							>
								<ChevronDownIcon className="size-3.5" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-56">
							<DropdownMenuLabel>Chat</DropdownMenuLabel>
							<DropdownMenuItem
								onClick={() => {
									onOpenChat();
									onNewChat();
								}}
							>
								<MessageSquarePlusIcon className="size-4" />
								New Mairko chat
							</DropdownMenuItem>
							<DropdownMenuItem onClick={onOpenChat}>
								<MessageSquareIcon className="size-4" />
								Open chat
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuLabel>Specialist (Desk)</DropdownMenuLabel>
							{askable.map((agent) => {
								const locked =
									!setupDone && agent.id !== "onboarding";
								return (
									<DropdownMenuItem
										key={agent.id}
										disabled={locked}
										onClick={() => onAskAgent(agent.id)}
									>
										<AgentAvatar agent={agent} size="sm" />
										<span className="min-w-0 flex-1 truncate">
											{agent.name}
											<span className="ml-1 text-muted-foreground">
												{agent.department}
											</span>
										</span>
									</DropdownMenuItem>
								);
							})}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</div>
		</div>
	);
}
